import React, { useState, useEffect } from "react";
import "./PlanSelectionModal.scss";
import { FaTimes, FaCheckCircle, FaExclamationTriangle, FaArrowUp, FaArrowDown, FaEquals } from "react-icons/fa";
import {
  getAllExistPlans,
  createPaymentOrder,
} from "../../services/api/SuperAdminApi";
import { getCompanyDataByID } from "../../services/api/CompanyApi";
import { useAuth } from "../../context/AuthContext";
import { formatText } from "../TruncateParties";
import { saveContacForm } from "../../services/api/ContactApi";
import { useToast } from "../../context/ToastContext";

// Contact Form Component
const ContactForm = ({ companyData, onClose, currentPlan }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    comment: "",
    phno: "",
    callMe: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });

  // Pre-fill form with company data if available
  useEffect(() => {
    if (companyData) {
      setFormData((prev) => ({
        ...prev,
        name: companyData.companyName || "",
        email: companyData.email || "",
        phno: companyData.phno || "",
      }));
    }
  }, [companyData]);

  const validateForm = () => {
    if (!formData.name.trim()) {
      setNotification({ type: "warning", message: "Please enter your name." });
      return false;
    }
    if (!formData.email.trim()) {
      setNotification({ type: "warning", message: "Please enter your email address." });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setNotification({ type: "warning", message: "Please enter a valid email address." });
      return false;
    }
    if (!formData.subject.trim()) {
      setNotification({ type: "warning", message: "Please enter a subject." });
      return false;
    }
    if (!formData.comment.trim()) {
      setNotification({ type: "warning", message: "Please enter your comment." });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setNotification({ type: "", message: "" });

    try {


      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        comment: formData.comment.trim(),
        source: "lawsuit-pro-website",
        phno: formData.phno.trim(),
        callMe: formData.callMe
      };

      const response = await saveContacForm(payload);

      if (response.status === 200 || response.status === 201) {
        setNotification({
          type: "success",
          message: "Message sent successfully! We will get back to you soon."
        });

        // Clear form after successful submission
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            subject: "",
            comment: "",
            phno: "",
            callMe: false,
          });
          // Close modal after 2 seconds
          setTimeout(() => {
            onClose();
          }, 2000);
        }, 1000);
      } else {
        setNotification({
          type: "error",
          message: "Failed to send message. Please try again."
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);

      let errorMessage = 'Failed to send message. Please try again.';

      if (error.message && error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (error.response && error.response.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      setNotification({ type: "error", message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="premium-contact-form-container">
      <div className="form-header">
        <h3>Contact Our Support Team</h3>
        <p>
          {/* You are already on our {currentPlan?.name || "current"} plan. For any modifications or
          inquiries, please fill out the form below and our team will get back to you. */}
          You are currently subscribed to the <strong>{currentPlan?.name || "current"}</strong> plan. For any changes or inquiries, please complete the form below and our support team will reach out to you shortly.
        </p>
      </div>

      {notification.message && (
        <div className={`form-notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === "success" && <FaCheckCircle size={20} />}
            {notification.type === "error" && <FaExclamationTriangle size={20} />}
            {notification.type === "warning" && <FaExclamationTriangle size={20} />}
          </div>
          <p>{notification.message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="contact-form">
        <div className="form-row">
          <div className="form-group">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your Name*"
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email Address*"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <input
              type="tel"
              name="phno"
              value={formData.phno}
              onChange={handleInputChange}
              placeholder="Phone Number*"
              disabled={isSubmitting}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Subject*"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>
        
        <div className="form-group">
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            placeholder="Write us your comment*"
            rows="5"
            disabled={isSubmitting}
            required
          />
        </div>
        
        <div className="form-group" style={{marginTop: '12px'}}>
          <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
            <input
              type="checkbox"
              name="callMe"
              checked={formData.callMe}
              onChange={handleInputChange}
              disabled={isSubmitting}
              style={{cursor: 'pointer', width: '16px', height: '16px'}}
            />
            <span style={{fontSize: '14px'}}>Want us to Connect?</span>
          </label>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending Message..." : "Send Message"}
          </button>
          <p className="go-back">
            Need to go back?{" "}
            <button
              type="button"
              onClick={onClose}
              className="back-link"
              disabled={isSubmitting}
            >
              Go Back
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

const PlanComparisonModal = ({ currentPlan, selectedPlan, onConfirm, onCancel,billingPeriod }) => {
  const featureLabels = {
    cases: "Cases Management",
    clients: "Client Management",
    expenses: "Expense Tracking",
    invoices: "Invoicing",
    labels: "Labels & Tags",
    notes: "Notes",
    tasks: "Task Management",
    teams: "Team Collaboration",
    branch: "Branch Management",
    mobileAppAccess: "Mobile App Access"
  };

  const getFeatureStatus = (hasFeature) => hasFeature ? "✓" : "✗";
  const getFeatureClass = (hasFeature) => hasFeature ? "feature-enabled" : "feature-disabled";
  const getPeriodText = (duration) => {
    return duration === 365 ? "year" : "month";
  };

  return (
    <div className="comparison-modal-overlay" onClick={onCancel}>
      <div className="comparison-modal" onClick={(e) => e.stopPropagation()}>
        <div className="comparison-header">
          <h3>Upgrade Plan Comparison</h3>
          <p>See what changes with your new plan</p>
        </div>

        <div className="comparison-content">
          <div className="comparison-grid">
            {/* Current Plan Column */}
            <div className="plan-column current">
              <div className="column-header">
                <h4>Current Plan</h4>
                <p className="plan-name">{currentPlan.name}</p>
                <p className="plan-price">₹{currentPlan.price.toLocaleString()}/{getPeriodText(currentPlan.duration)}</p>
              </div>
              <div className="column-body">
                <div className="limits-section">
                  <div className="limit-item">
                    <span className="limit-label">Cases Limit:</span>
                    <span className="limit-value">{currentPlan.caseLimit}</span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">User Limit:</span>
                    <span className="limit-value">{currentPlan.userLimit}</span>
                  </div>
                </div>
                <div className="features-section">
                  <h5>Features</h5>
                  {Object.entries(featureLabels).map(([key, label]) => (
                    <div key={key} className={`feature-item ${getFeatureClass(currentPlan.rights[key])}`}>
                      <span className="feature-icon">{getFeatureStatus(currentPlan.rights[key])}</span>
                      <span className="feature-label">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="arrow-column">
              <div className="upgrade-arrow">→</div>
              <span className="upgrade-text">UPGRADE</span>
            </div>

            {/* New Plan Column */}
            <div className="plan-column new">
              <div className="column-header highlighted">
                <h4>New Plan</h4>
                <p className="plan-name">{selectedPlan.name}</p>
                <p className="plan-price">₹{selectedPlan.price.toLocaleString()}/{getPeriodText(selectedPlan.duration)}</p>
                <span className="upgrade-badge">Upgraded</span>
              </div>
              <div className="column-body">
                <div className="limits-section">
                  <div className="limit-item">
                    <span className="limit-label">Cases Limit:</span>
                    <span className={`limit-value ${selectedPlan.caseLimit > currentPlan.caseLimit ? 'improved' : ''}`}>
                      {selectedPlan.caseLimit}
                      {selectedPlan.caseLimit > currentPlan.caseLimit && (
                        <span className="increase">+{selectedPlan.caseLimit - currentPlan.caseLimit}</span>
                      )}
                    </span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-label">User Limit:</span>
                    <span className={`limit-value ${selectedPlan.userLimit > currentPlan.userLimit ? 'improved' : ''}`}>
                      {selectedPlan.userLimit}
                      {selectedPlan.userLimit > currentPlan.userLimit && (
                        <span className="increase">+{selectedPlan.userLimit - currentPlan.userLimit}</span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="features-section">
                  <h5>Features</h5>
                  {Object.entries(featureLabels).map(([key, label]) => {
                    const isNew = !currentPlan.rights[key] && selectedPlan.rights[key];
                    return (
                      <div key={key} className={`feature-item ${getFeatureClass(selectedPlan.rights[key])} ${isNew ? 'new-feature' : ''}`}>
                        <span className="feature-icon">{getFeatureStatus(selectedPlan.rights[key])}</span>
                        <span className="feature-label">{label}</span>
                        {isNew && <span className="new-badge">NEW</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="comparison-footer">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="confirm-button" onClick={onConfirm}>
            Confirm Upgrade
          </button>
        </div>
      </div>
    </div>
  );
};


const PlanSelectionModal = ({ isOpen, companyId, onClose, onSelectPlan }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [isPremiumRedirect, setIsPremiumRedirect] = useState(false);
    const [showComparison, setShowComparison] = useState(false);
  const [tempSelectedPlan, setTempSelectedPlan] = useState(null);
  const { logout } = useAuth();
  const { showToast } = useToast();
  
  // Function to get ACTUAL current plan data (not affected by billing period selection)
  const getActualCurrentPlanData = () => {
    if (!companyData?.company?.planId || !plans.length) return null;
    
    // First, try to find the plan by planId (this is the actual subscribed plan)
    const currentPlanById = plans.find(plan => plan._id === companyData.company.planId);
    
    // If found, return it directly
    if (currentPlanById) {
      return currentPlanById;
    }
    
    // Fallback: Use planData from company response (this has the actual plan details)
    if (companyData?.planData) {
      return companyData.planData;
    }
    
    return null;
  };

  // Function to handle plan selection with comparison
  // const handlePlanClick = (plan) => {
  //   if (shouldDisable(plan.type)) return;

  //   // For Enterprise, show contact form
  //   if (plan.type === 'ent') {
  //     setIsPremiumRedirect(true);
  //     return;
  //   }

  //   // Check if it's an upgrade
  //   const planHierarchy = { ind: 1, std: 2, prm: 3, ent: 4 };
  //   const currentLevel = planHierarchy[companyData?.type] || 0;
  //   const selectedLevel = planHierarchy[plan.type] || 0;

  //   if (selectedLevel > currentLevel && companyData?.planId) {
  //     // Show comparison modal for upgrades
  //     setTempSelectedPlan(plan);
  //     setShowComparison(true);
  //   } else {
  //     // Direct selection for non-upgrades
  //     setSelectedPlan(plan);
  //   }
  // };

  // Function to handle plan selection with comparison
const handlePlanClick = (plan) => {
  // First check if plan should be disabled (existing logic)
  if (shouldDisable(plan.type)) return;

  // For Enterprise, show contact form (existing logic)
  if (plan.type === 'ent') {
    setIsPremiumRedirect(true);
    return;
  }

  // Check if it's an upgrade
  const planHierarchy = { ind: 1, std: 2, prm: 3, ent: 4 };
  const currentLevel = planHierarchy[companyData?.company?.type] || 0;
  const selectedLevel = planHierarchy[plan.type] || 0;

  // Get ACTUAL current plan data (not affected by billing period selection)
  const currentPlanData = getActualCurrentPlanData();

  // Only show comparison for UPGRADES (higher tier than current)
  // And only if we have current plan data to compare with
  if (selectedLevel > currentLevel && currentPlanData) {
    
    // Show comparison modal for upgrades
    setTempSelectedPlan(plan);
    setShowComparison(true);
  } else {
    setSelectedPlan(plan);
  }
};


  // Function to confirm upgrade
  const handleConfirmUpgrade = () => {
    setSelectedPlan(tempSelectedPlan);
    setShowComparison(false);
    setTempSelectedPlan(null);
  };

  // Function to cancel comparison
  const handleCancelComparison = () => {
    setShowComparison(false);
    setTempSelectedPlan(null);
  };


  // ... rest of existing code

  const GST_RATE = 0.18;

  const calculateGST = (basePrice) => {
    return Math.round(basePrice * GST_RATE);
  };

  // Function to handle closing and state reset
  const handleClose = () => {
    onClose(); // Close the modal
    // Reset all local state for the next time it opens
    setIsPremiumRedirect(false);
    setCompanyData(null);
    setLoading(true);
    setPaymentStatus(null);
    setStatusMessage("");
    setSelectedPlan(null);
    setTermsAccepted(false);
  };

  // Check if subscription is expired
  const isSubscriptionExpired = () => {
    if (!companyData?.company?.subscription?.currentPeriodEnd) return false;

    const endDate = new Date(companyData.company.subscription.currentPeriodEnd);
    const now = new Date();

    return endDate < now;
  };

  // Check if the plan is the user's current plan
  const isCurrentPlan = (planType) => {
    return companyData?.company?.type === planType;
  };

  // Determine if a plan should be disabled
  const shouldDisable = (planType) => {
    if (!companyData?.company?.type) return false;

    const planHierarchy = { ind: 1, std: 2, prm: 3, ent: 4 };
    const currentLevel = planHierarchy[companyData.company.type] || 0;
    const checkingLevel = planHierarchy[planType] || 0;

    const isExpired = isSubscriptionExpired();

    if (isExpired) {
      // When expired, only disable plans lower than current plan
      return checkingLevel < currentLevel;
    } else {
      // When active, disable current plan and lower tier plans
      return checkingLevel <= currentLevel;
    }
  };

  // Determine if the "Current Plan" badge should be shown
  const shouldShowCurrentBadge = (planType) => {
    if (!companyData?.company?.type) return false;

    const isExpired = isSubscriptionExpired();

    // Show "Current Plan" badge only when expired and it's the current plan
    return isExpired && isCurrentPlan(planType);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch plans
        const plansResponse = await getAllExistPlans("");
        const allPlans = plansResponse.data;

        // Filter plans by type and duration
        const filteredPlans = allPlans.filter((plan) =>
          ["ind", "std", "prm", "ent"].includes(plan.type)
        );

        setPlans(filteredPlans);

        // Fetch company data
        const companyResponse = await getCompanyDataByID(companyId);
        // Store the complete response data (includes both company and planData)
        setCompanyData(companyResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    // This effect now only fetches data on open
    if (isOpen && companyId) {
      fetchData();
    }
  }, [isOpen, companyId]);

  // Check for premium/enterprise plan redirect - only if subscription is ACTIVE
  useEffect(() => {
    if (!loading && (companyData?.company?.type === "prm" || companyData?.company?.type === "ent")) {
      const isExpired = isSubscriptionExpired();

      // Only show contact form if subscription is NOT expired
      if (!isExpired) {
        setIsPremiumRedirect(true);
      } else {
        setIsPremiumRedirect(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData, loading]);

  const getFilteredPlans = () => {
    const duration = billingPeriod === "yearly" ? 365 : 31;
    return plans.filter((plan) => plan.duration === duration);
  };

  const getPlansByType = () => {
    const filteredPlans = getFilteredPlans();
    const planTypes = {
      ind: null,
      std: null,
      prm: null,
      ent: null,
    };

    // Static plan IDs for yearly plans
    const yearlyPlanIds = {
      ind: '683441ac9c7ec79f8cb0424b',
      std: '683416c477d22bdee872575b',
      prm: '6837ec6bae38e188245b1664',
      ent: '6837f092ae38e188245b1669'
    };

    // If billing period is yearly, use static plan IDs
    if (billingPeriod === "yearly") {
      filteredPlans.forEach((plan) => {
        if (plan.type === 'ind' && plan._id === yearlyPlanIds.ind) {
          planTypes.ind = plan;
        } else if (plan.type === 'std' && plan._id === yearlyPlanIds.std) {
          planTypes.std = plan;
        } else if (plan.type === 'prm' && plan._id === yearlyPlanIds.prm) {
          planTypes.prm = plan;
        } else if (plan.type === 'ent' && plan._id === yearlyPlanIds.ent) {
          planTypes.ent = plan;
        }
      });
    } else {
      // For monthly plans, use the existing logic
      filteredPlans.forEach((plan) => {
        if (!planTypes[plan.type]) {
          planTypes[plan.type] = plan;
        }
      });
    }
    
    return planTypes;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const shouldHighlight = (planType) => {
    if (!companyData?.company?.type) return false;

    const planHierarchy = { ind: 1, std: 2, prm: 3, ent: 4 };
    const currentLevel = planHierarchy[companyData.company.type] || 0;
    const checkingLevel = planHierarchy[planType] || 0;

    return checkingLevel > currentLevel;
  };

  const shouldBlur = (planType) => {
    if (!companyData?.company?.type) return false;

    const planHierarchy = { ind: 1, std: 2, prm: 3, ent: 4 };
    const currentLevel = planHierarchy[companyData.company.type] || 0;
    const checkingLevel = planHierarchy[planType] || 0;

    // Don't blur the current plan, only blur lower tier plans
    return checkingLevel < currentLevel;
  };

  const handleMakePayment = async () => {
    if (!selectedPlan || !termsAccepted || !companyId) {
      //  alert("Please select a plan and accept the terms and conditions.");
      showToast("Please select a plan and accept the terms and conditions.","error");
      return;
    }

    try {
      setLoading(true);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert(
          "Payment system failed to load. Please check your internet connection."
        );
        setLoading(false);
        return;
      }


      const baseAmount = selectedPlan.price;
      const gstAmount = calculateGST(baseAmount);
      const totalAmount = baseAmount + gstAmount;
      const totalAmountInPaise = totalAmount * 100; // Convert to paise for Razorpay


      // Get Order Data
      const orderResponse = await createPaymentOrder(
        selectedPlan._id,
        companyId
      );

      // Validate server response
      if (!orderResponse.data || typeof orderResponse.data !== "object") {
        throw new Error("Invalid response structure from payment API");
      }

      const { orderId, currency, key } = orderResponse.data;

      // Validate required fields (note: we're using our calculated amount, not server's)
      if (!orderId || !currency || !key) {
        alert("Payment system error. Please try again or contact support.");
        console.error("Missing required fields:", {
          orderId,
          currency,
          key,
        });
        setLoading(false);
        return;
      }

      // Ensure amount is valid
      if (isNaN(totalAmountInPaise) || totalAmountInPaise <= 0) {
        alert("Invalid payment amount. Please try again.");
        console.error("Invalid amount:", totalAmountInPaise);
        setLoading(false);
        return;
      }

      setLoading(false);

      // Razorpay options
      const options = {
        key: String(key).trim(),
        amount: totalAmountInPaise,
        currency: String(currency).trim().toUpperCase(),
        name: "Lawsuit Pro",
        description: `Plan Subscription Payment (incl. 18% GST)`,
        order_id: String(orderId).trim(),
        handler: async function (response) {

          // Check if razorpay_payment_id exists
          if (response.razorpay_payment_id) {
            // Payment successful
            setPaymentStatus("success");
            setStatusMessage(
              "Payment successful! Logging out and redirecting..."
            );
            setLoading(true);

            // Call the parent handler with success
            if (onSelectPlan) {
              onSelectPlan(selectedPlan, companyId, {
                ...orderResponse.data,
                razorpayResponse: response,
              });
            }

            // Logout and redirect after 2 seconds
            setTimeout(() => {
              logout();
              setTimeout(() => {
                window.location.href = "https://app.lawsuit-pro.com/";
              }, 500);
            }, 2000);
          } else {
            // Payment ID not found - something went wrong
            setPaymentStatus("error");
            setStatusMessage("Something went wrong. Please contact admin.");
            setLoading(false);
            console.error("Payment ID not found in response:", response);
          }
        },
        prefill: {
          name: companyData?.company?.companyName || "Company User",
          email: companyData?.company?.email || "",
          contact: companyData?.company?.phno || "",
        },
        theme: {
          color: "#004A9A",
        },
        modal: {
          ondismiss: function () {
            setPaymentStatus("error");
            setStatusMessage("Payment cancelled.");
            setLoading(false);
          },
        },
      };


      // Initialize and open Razorpay
      try {
        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", function (response) {
          console.error("=== Payment Failed ===");
          console.error("Error Response:", response);
          setPaymentStatus("error");
          setStatusMessage(
            "Payment failed. Please try again or contact support."
          );
          setLoading(false);
        });

        rzp.open();
      } catch (razorpayError) {
        console.error("=== Razorpay Initialization Error ===");
        console.error("Error:", razorpayError);
        setPaymentStatus("error");
        setStatusMessage(
          "Failed to initialize payment gateway. Please try again."
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("=== Complete Error Details ===");
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);

      if (error.response) {
        console.error("Server Error Response:", error.response.data);
        console.error("Status Code:", error.response.status);
      }

      // Better error messages
      let errorMessage =
        "An error occurred during payment processing. Please try again.";

      if (error.message.includes("timeout")) {
        errorMessage = "Request timed out. Please try again in a few moments.";
      } else if (error.message.includes("Network Error")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.response && error.response.status >= 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.response && error.response.status === 404) {
        errorMessage = "Service not found. Please contact support.";
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      }

      setPaymentStatus("error");
      setStatusMessage(errorMessage);
      setLoading(false);
    }
  };

  const getCurrentPlanName = () => {
    if (!companyData?.planData) return "N/A";
    return companyData.planData.name || "N/A";
  };

  if (!isOpen) return null;

  const plansByType = getPlansByType();

  return (
    <div className="plan-selection-modal-overlay">
      <div className="plan-selection-modal">
        <div className="modal-header">
          <button
            className="close-button"
            onClick={handleClose}
            disabled={paymentStatus === "success"}
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <p>
              {paymentStatus === "success"
                ? "Processing payment..."
                : "Loading plans..."}
            </p>
          </div>
        ) : (
          <>
            <div className="company-info">
              <h2>{formatText(companyData?.company?.companyName) || "Company"}</h2>
              <div className="contact-info">
                <span>Email: {companyData?.company?.email || "N/A"}</span>
                <span> | </span>
                <span>Phone: {companyData?.company?.phno || "N/A"}</span>
              </div>
              <div className="current-plan">
                <span>
                  Current Plan: <strong>{getCurrentPlanName()}</strong>
                </span>
              </div>
            </div>

            {isPremiumRedirect ? (
              <ContactForm
                companyData={companyData?.company}
                onClose={handleClose}
                currentPlan={companyData?.planData}
              />
            ) : (
              <div className="plan-content">
                <h3 className="section-title">Select Plan *</h3>

                <div className="plans-grid">
                  {plansByType.ind && (
                    <div
                      className={`plan-card ${selectedPlan?._id === plansByType.ind._id
                          ? "selected"
                          : ""
                        } ${shouldBlur("ind") ? "blurred" : ""} ${shouldHighlight("ind") ? "highlighted" : ""
                        } ${shouldDisable("ind") ? "disabled" : ""}`}
                      onClick={() => {
                        // if (!shouldDisable("ind")) {
                        //   setSelectedPlan(plansByType.ind);
                        // }
                        handlePlanClick(plansByType.ind)
                      }}
                    >
                      {selectedPlan?._id === plansByType.ind._id && (
                        <FaCheckCircle className="checkmark" />
                      )}
                      {shouldHighlight("ind") && !shouldShowCurrentBadge("ind") && (
                        <span className="upgrade-badge">Upgrade</span>
                      )}
                      {shouldShowCurrentBadge("ind") && (
                        <span className="current-plan-badge">Current Plan</span>
                      )}
                      <h4>Individual</h4>
                      <div className="price-container">
                        <p className="base-price">
                          ₹{plansByType.ind.price.toLocaleString()}/
                          {billingPeriod === "yearly" ? "year" : "month"}
                        </p>
                        <p className="gst-info">
                          + GST ₹{calculateGST(plansByType.ind.price).toLocaleString()}
                        </p>
                        <p className="total-price">
                          Total: ₹{(plansByType.ind.price + calculateGST(plansByType.ind.price)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {plansByType.std && (
                    <div
                      className={`plan-card ${selectedPlan?._id === plansByType.std._id
                          ? "selected"
                          : ""
                        } ${shouldBlur("std") ? "blurred" : ""} ${shouldHighlight("std") ? "highlighted" : ""
                        } ${shouldDisable("std") ? "disabled" : ""}`}
                      onClick={() => {
                        // if (!shouldDisable("std")) {
                        //   setSelectedPlan(plansByType.std);
                        // }
                        handlePlanClick(plansByType.std)
                      }}
                    >
                      {selectedPlan?._id === plansByType.std._id && (
                        <FaCheckCircle className="checkmark" />
                      )}
                      {shouldHighlight("std") && !shouldShowCurrentBadge("std") && (
                        <span className="upgrade-badge">Upgrade</span>
                      )}
                      {shouldShowCurrentBadge("std") && (
                        <span className="current-plan-badge">Current Plan</span>
                      )}
                      <h4>Standard</h4>
                      <div className="price-container">
                        <p className="base-price">
                          ₹{plansByType.std.price.toLocaleString()}/
                          {billingPeriod === "yearly" ? "year" : "month"}
                        </p>
                        <p className="gst-info">
                          + GST ₹{calculateGST(plansByType.std.price).toLocaleString()}
                        </p>
                        <p className="total-price">
                          Total: ₹{(plansByType.std.price + calculateGST(plansByType.std.price)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {plansByType.prm && (
                    <div
                      className={`plan-card ${selectedPlan?._id === plansByType.prm._id
                          ? "selected"
                          : ""
                        } ${shouldBlur("prm") ? "blurred" : ""} ${shouldHighlight("prm") ? "highlighted" : ""
                        } ${shouldDisable("prm") ? "disabled" : ""}`}
                      onClick={() => {
                        // if (!shouldDisable("prm")) {
                        //   setSelectedPlan(plansByType.prm);
                        // }
                        handlePlanClick(plansByType.prm)
                      }}
                    >
                      {selectedPlan?._id === plansByType.prm._id && (
                        <FaCheckCircle className="checkmark" />
                      )}
                      {shouldHighlight("prm") && !shouldShowCurrentBadge("prm") && (
                        <span className="upgrade-badge">Upgrade</span>
                      )}
                      {shouldShowCurrentBadge("prm") && (
                        <span className="current-plan-badge">Current Plan</span>
                      )}
                      <h4>Premium</h4>
                      <div className="price-container">
                        <p className="base-price">
                          ₹{plansByType.prm.price.toLocaleString()}/
                          {billingPeriod === "yearly" ? "year" : "month"}
                        </p>
                        <p className="gst-info">
                          + GST ₹{calculateGST(plansByType.prm.price).toLocaleString()}
                        </p>
                        <p className="total-price">
                          Total: ₹{(plansByType.prm.price + calculateGST(plansByType.prm.price)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {plansByType.ent && (
                    <div
                      className={`plan-card ${selectedPlan?._id === plansByType.ent._id
                          ? "selected"
                          : ""
                        } ${shouldBlur("ent") ? "blurred" : ""} ${shouldHighlight("ent") ? "highlighted" : ""
                        } ${shouldDisable("ent") ? "disabled" : ""}`}
                      onClick={() => {
                        // if (!shouldDisable("ent")) {
                        //   // Show contact form for Enterprise plan
                        //   setIsPremiumRedirect(true);
                        // }
                        handlePlanClick(plansByType.ent)
                      }}
                    >
                      {selectedPlan?._id === plansByType.ent._id && (
                        <FaCheckCircle className="checkmark" />
                      )}
                      {shouldHighlight("ent") && !shouldShowCurrentBadge("ent") && (
                        <span className="upgrade-badge">Upgrade</span>
                      )}
                      {shouldShowCurrentBadge("ent") && (
                        <span className="current-plan-badge">Current Plan</span>
                      )}
                      <h4>Enterprise</h4>
                      <div className="price-container enterprise-modern">
                        {/* <p className="enterprise-main">Custom Plan</p> */}
                        <p className="enterprise-benefits">
                          Designed for your organization's unique requirements
                        </p>
                        <div className="contact-badge">Get Your Quote</div>
                      </div>
                    </div>
                  )}


                </div>

                {/* <div className="billing-period-section">
                  <label htmlFor="billing-period">Billing Period *</label>
                  <select
                    id="billing-period"
                    value={billingPeriod}
                    onChange={(e) => {
                      setBillingPeriod(e.target.value);
                      setSelectedPlan(null); // Reset selection when changing period
                    }}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div> */}

                <div className="terms-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>
                      I have read and accept the{" "}
                      <a href="https://lawsuit-pro.com/single-career.html" className="terms-link"
                        target="_blank" rel="noopener noreferrer"
                      >
                        terms and conditions
                      </a>
                    </span>
                  </label>
                </div>

                {/* Payment Status Message */}
                {paymentStatus && (
                  <div className={`payment-status-message ${paymentStatus}`}>
                    <div className="status-icon">
                      {paymentStatus === "success" && (
                        <FaCheckCircle size={24} />
                      )}
                      {paymentStatus === "error" && (
                        <FaExclamationTriangle size={24} />
                      )}
                    </div>
                    <p>{statusMessage}</p>
                  </div>
                )}

                <div className="modal-footer">
                  <button
                    className="payment-button"
                    onClick={handleMakePayment}
                    disabled={
                      !selectedPlan ||
                      !termsAccepted ||
                      loading ||
                      paymentStatus === "success"
                    }
                  >
                    {loading ? "Processing..." : "Make Payment"}
                  </button>
                  <p className="go-back">
                    Need to go back?{" "}
                    <button
                      onClick={handleClose}
                      className="back-link"
                      disabled={paymentStatus === "success"}
                    >
                      Go Back
                    </button>
                  </p>
                </div>
              </div>
            )}
          </>
        )}
        {showComparison && tempSelectedPlan && getActualCurrentPlanData() && (
        <PlanComparisonModal
          currentPlan={getActualCurrentPlanData()}
          selectedPlan={tempSelectedPlan}
          onConfirm={handleConfirmUpgrade}
          onCancel={handleCancelComparison}
        />
      )}
      </div>
    </div>
  );
};

export default PlanSelectionModal;